import { useCallback, useMemo, useRef, useState } from "react";
import type { ProvetLoadStatus } from "../../../context/provetDataContext";
import {
  AppointmentTime,
  ProvetDepartment,
  ProvetMonthModel,
  Reason
} from "../../../types";

interface CalendarStateProps {
  /** Optional initial date to start calendar on (defaults to today) */
  initialDate?: Date;
  /** Language code for locale-specific formatting (e.g., 'en', 'sv', 'de') */
  language: string;
}

/**
 * Calendar state management hook for the appointment time picker.
 *
 * Manages all stateful aspects of the calendar UI including:
 * - Selected date and veterinarian
 * - Available appointment times for selected day
 * - Month model with week/day structure
 * - Load status of the available times
 * - Previous values tracking for change detection
 *
 * **State Variables:**
 * - `selectedDate` - Currently selected calendar date
 * - `selectedVetId` - ID of selected veterinarian (0 = any veterinarian)
 * - `timesStatus` - Whether the month's times are loading, loaded or failed to load
 * - `availableAppointmentTimesForSelectedDay` - Time slots for selected date
 * - `provetMonth` - Calendar month structure with weeks/days/availability
 * - `firstAvailableDate` - The first date with times after an empty month, if any
 * - `currentLocale` - Locale string derived from language prop
 *
 * **Refs:**
 * - `selectedVetIdRef` - The selected veterinarian, up to date before the next render, so a
 *   time list built right after picking a vet uses that vet (observation 22)
 * - `loadedMonthRef` - The month whose times have loaded; null until then and after a failure
 * - `previousDepartmentRef` - Tracks department changes to trigger refresh
 * - `previousReasonRef` - Tracks treatment changes to trigger refresh
 *
 * @param props - Configuration with optional initial date and required language
 * @returns Object containing all state variables, setters, refs, and locale
 *
 * @example
 * ```tsx
 * const calendarState = useCalendarState({ language: 'en' });
 *
 * // Update selected date
 * calendarState.setSelectedDate(new Date('2024-12-25'));
 *
 * // Check if loading
 * if (calendarState.timesStatus === "loading") {
 *   return <Spinner />;
 * }
 *
 * // Access available times
 * const times = calendarState.availableAppointmentTimesForSelectedDay;
 * ```
 */
export function useCalendarState(props: CalendarStateProps) {
  const [selectedDate, setSelectedDate] = useState<Date>(props.initialDate || new Date());
  const [selectedVetId, setSelectedVetIdState] = useState<number>(0);
  const selectedVetIdRef = useRef<number>(0);
  const setSelectedVetId = useCallback((vetId: number) => {
    selectedVetIdRef.current = vetId;
    setSelectedVetIdState(vetId);
  }, []);
  const [timesStatus, setTimesStatus] = useState<ProvetLoadStatus>("loading");
  // Why the times failed to load, shown while `timesStatus` is "error"
  const [timesLoadError, setTimesLoadError] = useState<unknown>(null);
  const [
    availableAppointmentTimesForSelectedDay,
    setAvailableAppointmentTimesForSelectedDay
  ] = useState<AppointmentTime[]>([]);
  const [provetMonth, setProvetMonth] = useState<ProvetMonthModel | null>(null);
  const loadedMonthRef = useRef<ProvetMonthModel | null>(null);
  const [firstAvailableDate, setFirstAvailableDate] = useState<Date | undefined>();

  // Refs to track previous values for change detection
  const previousDepartmentRef = useRef<ProvetDepartment | null>(null);
  const previousReasonRef = useRef<Reason | null>(null);

  // Use the language prop passed from the server - this ensures the calendar weekdays
  // match the site's language rather than the browser's language
  const currentLocale = useMemo(() => props.language, [props.language]);

  return {
    selectedDate,
    setSelectedDate,
    selectedVetId,
    setSelectedVetId,
    selectedVetIdRef,
    timesStatus,
    setTimesStatus,
    timesLoadError,
    setTimesLoadError,
    availableAppointmentTimesForSelectedDay,
    setAvailableAppointmentTimesForSelectedDay,
    provetMonth,
    setProvetMonth,
    loadedMonthRef,
    firstAvailableDate,
    setFirstAvailableDate,
    currentLocale,
    previousDepartmentRef,
    previousReasonRef
  };
}
